import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import { addInsightToOutbox, type OutboxInsight } from './ecwOutbox';

/**
 * 気づきカード機能 (Sprint X5c minimum、REQ-4.2.1)。
 *
 * Path: `/districts/{districtId}/ecwInsights/{insightId}` (docs/04 §4-2)。
 *
 * minimum 仕様:
 *   - onSnapshot で list subscribe (createdAt desc)
 *   - createInsight: 手動入力 (title / observations / diagnosis / sentiment / selectedPostIds) + serverTimestamp
 *   - deleteInsight: 本人のみ (rules で enforce)
 *   - hidden=true (admin モデレーション) は client 側で filter
 *
 * 旧 InsightList との差分:
 *   - AI 対話 (Step1-2) は scope 外 (M4 aiDialogueSettings 連動は Sprint X5d 以降)
 *   - llmAnalysis / llmResolutionQuestions / llmDiagnosisQuestions は default 値で書込
 *   - team / categoryLabel / emotionTag / initialThoughts は scope 外
 */

export interface InsightMinimal {
  id: string;
  districtId: string;
  userId: string;
  title: string;
  observations: string;
  diagnosis: string;
  sentiment: number;
  selectedPostIds: string[];
  nickname: string;
  hidden: boolean;
  createdAt: Timestamp | null;
  /* Phase B 追加: AI 対話ログ + 起動 context (back モード Insight 要約や詳細表示で参照)。 */
  initialThoughts?: string;
  llmAnalysis?: string;
  llmResolutionQuestions?: string[];
  llmDiagnosisQuestions?: string[];
  emotionTag?: string;
  categoryLabel?: string;
  team?: string;
  /** キャッチ画像 (キービジュアル、dataURL or http URL)。気づきの代表画像 (旧 idea と同形式)。 */
  referenceImageBase64?: string;
  /** メインセッション ID (作成時の membership.mainSessionId)。表示はセッション名に解決。 */
  sessionId?: string | null;
}

export type InsightsState =
  | { status: 'loading'; items: []; error: null }
  | { status: 'ready'; items: InsightMinimal[]; error: null }
  | { status: 'error'; items: []; error: Error };

export interface CreateInsightInput {
  userId: string;
  nickname: string;
  title: string;
  observations: string;
  diagnosis: string;
  /** -1..+1。範囲外は clamp。 */
  sentiment: number;
  selectedPostIds: string[];
  /* === Phase B 拡張: 旧 EcwInsight full schema 互換 (全 optional、未指定なら省略) === */
  /** Step0 でユーザーが書いた最初の感想 (空なら省略)。 */
  initialThoughts?: string;
  /** AI Step1 出力 (analysis)。 */
  llmAnalysis?: string;
  /** AI Step1 出力 (resolutionQuestions[])。 */
  llmResolutionQuestions?: string[];
  /** AI Step2 出力 (diagnosisQuestions[])。 */
  llmDiagnosisQuestions?: string[];
  /** 起動時 context (Plutchik 8 感情)。 */
  emotionTag?: string;
  /** 起動時 context (categoryMaster 表示名)。 */
  categoryLabel?: string;
  /** 参加セッション名 join (旧踏襲)。 */
  team?: string;
  /** キャッチ画像 (キービジュアル、dataURL or http URL)。Step4 で選択 (空なら省略)。 */
  referenceImageBase64?: string;
  /** メインセッション ID (作成時の membership.mainSessionId、AnalysisPage が注入)。 */
  sessionId?: string | null;
}

function clampSentiment(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(-1, Math.min(1, v));
}

/**
 * Firestore に EcwInsight doc を直接書込 (online 時、または outbox flush 時に呼ばれる)。
 *
 * `useEcwInsights().createInsight` の online ブランチと、useEcwOutbox.flush の send
 * 経路で共有される実装本体。`addDoc` payload 構築 logic を hook 外に抽出することで、
 * outbox flush が React component lifecycle に依存しなくなる。
 */
export async function sendInsightToFirestore(
  input: CreateInsightInput & { districtId: string },
): Promise<string> {
  const col = collection(db, 'districts', input.districtId, 'ecwInsights');
  const now = serverTimestamp();
  const ref = await addDoc(col, {
    schemaVersion: 1,
    districtId: input.districtId,
    userId: input.userId,
    nickname: input.nickname,
    title: input.title.trim(),
    observations: input.observations.trim(),
    diagnosis: input.diagnosis.trim(),
    sentiment: clampSentiment(input.sentiment),
    selectedPostIds: input.selectedPostIds,
    hidden: false,
    createdAt: now,
    updatedAt: now,
    createdBy: input.userId,
    updatedBy: input.userId,
    archivedAt: null,
    ...(input.initialThoughts && input.initialThoughts.trim()
      ? { initialThoughts: input.initialThoughts.trim() }
      : {}),
    ...(input.llmAnalysis ? { llmAnalysis: input.llmAnalysis } : {}),
    ...(input.llmResolutionQuestions && input.llmResolutionQuestions.length > 0
      ? { llmResolutionQuestions: input.llmResolutionQuestions }
      : {}),
    ...(input.llmDiagnosisQuestions && input.llmDiagnosisQuestions.length > 0
      ? { llmDiagnosisQuestions: input.llmDiagnosisQuestions }
      : {}),
    ...(input.emotionTag ? { emotionTag: input.emotionTag } : {}),
    ...(input.categoryLabel ? { categoryLabel: input.categoryLabel } : {}),
    ...(input.team ? { team: input.team } : {}),
    ...(input.referenceImageBase64 ? { referenceImageBase64: input.referenceImageBase64 } : {}),
    ...(input.sessionId ? { sessionId: input.sessionId } : {}),
  });
  return ref.id;
}

/**
 * outbox 1 件 (insight) を Firestore に送信 (useEcwOutbox.flush から呼出)。
 * 成功時は新 insightId を返す (呼出側で deleteInsightFromOutbox)。
 * 失敗時は例外を throw (呼出側で attempts++ + lastError 記録)。
 */
export async function sendInsightOutboxItem(item: OutboxInsight): Promise<string> {
  return await sendInsightToFirestore({
    districtId: item.districtId,
    userId: item.userId,
    nickname: item.nickname,
    title: item.title,
    observations: item.observations,
    diagnosis: item.diagnosis,
    sentiment: item.sentiment,
    selectedPostIds: item.selectedPostIds,
    ...(item.initialThoughts ? { initialThoughts: item.initialThoughts } : {}),
    ...(item.llmAnalysis ? { llmAnalysis: item.llmAnalysis } : {}),
    ...(item.llmResolutionQuestions ? { llmResolutionQuestions: item.llmResolutionQuestions } : {}),
    ...(item.llmDiagnosisQuestions ? { llmDiagnosisQuestions: item.llmDiagnosisQuestions } : {}),
    ...(item.emotionTag ? { emotionTag: item.emotionTag } : {}),
    ...(item.categoryLabel ? { categoryLabel: item.categoryLabel } : {}),
    ...(item.team ? { team: item.team } : {}),
    ...(item.referenceImageBase64 ? { referenceImageBase64: item.referenceImageBase64 } : {}),
    ...(item.sessionId ? { sessionId: item.sessionId } : {}),
  });
}

export function useEcwInsights(
  // 地区フォールバック根絶: 地区未解決時は null。effect は no-op し、create/delete は呼ばれない。
  districtId: string | null,
  /**
   * 再試行用 key (Sprint AM、UI2 無限スピナー解消)。
   * 呼出側で setRetryKey(k=>k+1) するたびに subscribe を貼り直す。
   */
  retryKey: number = 0,
): {
  state: InsightsState;
  createInsight: (input: CreateInsightInput) => Promise<string>;
  deleteInsight: (insightId: string) => Promise<void>;
} {
  const [state, setState] = useState<InsightsState>({
    status: 'loading',
    items: [],
    error: null,
  });

  useEffect(() => {
    if (!districtId) {
      setState({ status: 'ready', items: [], error: null });
      return;
    }
    // retry 時に必ず loading に戻す
    setState({ status: 'loading', items: [], error: null });
    const col = collection(db, 'districts', districtId, 'ecwInsights');
    const q = query(col, orderBy('createdAt', 'desc'));
    const unsubscribe = onSnapshot(
      q,
      (snap) => {
        const items: InsightMinimal[] = snap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              districtId: (data.districtId as string) ?? districtId,
              userId: (data.userId as string) ?? '',
              title: (data.title as string) ?? '',
              observations: (data.observations as string) ?? '',
              diagnosis: (data.diagnosis as string) ?? '',
              sentiment: typeof data.sentiment === 'number' ? data.sentiment : 0,
              selectedPostIds: Array.isArray(data.selectedPostIds)
                ? (data.selectedPostIds as string[])
                : [],
              nickname: (data.nickname as string) ?? '',
              hidden: data.hidden === true,
              createdAt: (data.createdAt as Timestamp | undefined) ?? null,
              initialThoughts:
                typeof data.initialThoughts === 'string' ? data.initialThoughts : undefined,
              llmAnalysis: typeof data.llmAnalysis === 'string' ? data.llmAnalysis : undefined,
              llmResolutionQuestions: Array.isArray(data.llmResolutionQuestions)
                ? (data.llmResolutionQuestions as string[])
                : undefined,
              llmDiagnosisQuestions: Array.isArray(data.llmDiagnosisQuestions)
                ? (data.llmDiagnosisQuestions as string[])
                : undefined,
              emotionTag: typeof data.emotionTag === 'string' ? data.emotionTag : undefined,
              categoryLabel:
                typeof data.categoryLabel === 'string' ? data.categoryLabel : undefined,
              team: typeof data.team === 'string' ? data.team : undefined,
              referenceImageBase64:
                typeof data.referenceImageBase64 === 'string'
                  ? data.referenceImageBase64
                  : undefined,
              sessionId: typeof data.sessionId === 'string' ? data.sessionId : null,
            };
          })
          .filter((it) => !it.hidden);
        setState({ status: 'ready', items, error: null });
      },
      (error) => setState({ status: 'error', items: [], error }),
    );
    return unsubscribe;
  }, [districtId, retryKey]);

  const createInsight = useCallback(
    async (input: CreateInsightInput): Promise<string> => {
      if (!districtId) throw new Error('createInsight: districtId が未指定です (地区解決前)');
      const isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;
      if (!isOnline) {
        // offline: outbox にキュー、`outbox:<id>` 仮 ID を返却 (posts/usePosts.ts と同パターン)。
        const id = await addInsightToOutbox({
          createdLocalAt: Date.now(),
          districtId,
          userId: input.userId,
          nickname: input.nickname,
          title: input.title,
          observations: input.observations,
          diagnosis: input.diagnosis,
          sentiment: clampSentiment(input.sentiment),
          selectedPostIds: input.selectedPostIds,
          ...(input.initialThoughts ? { initialThoughts: input.initialThoughts } : {}),
          ...(input.llmAnalysis ? { llmAnalysis: input.llmAnalysis } : {}),
          ...(input.llmResolutionQuestions
            ? { llmResolutionQuestions: input.llmResolutionQuestions }
            : {}),
          ...(input.llmDiagnosisQuestions
            ? { llmDiagnosisQuestions: input.llmDiagnosisQuestions }
            : {}),
          ...(input.emotionTag ? { emotionTag: input.emotionTag } : {}),
          ...(input.categoryLabel ? { categoryLabel: input.categoryLabel } : {}),
          ...(input.team ? { team: input.team } : {}),
          ...(input.referenceImageBase64
            ? { referenceImageBase64: input.referenceImageBase64 }
            : {}),
          ...(input.sessionId ? { sessionId: input.sessionId } : {}),
          lastError: null,
          attempts: 0,
        });
        return `outbox:${id}`;
      }
      return await sendInsightToFirestore({ ...input, districtId });
    },
    [districtId],
  );

  const deleteInsight = useCallback(
    async (insightId: string): Promise<void> => {
      if (!districtId) throw new Error('deleteInsight: districtId が未指定です (地区解決前)');
      await deleteDoc(doc(db, 'districts', districtId, 'ecwInsights', insightId));
    },
    [districtId],
  );

  return { state, createInsight, deleteInsight };
}
